import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  BookedProducts,
  BookedService,
  BookingData,
  BookingListParams,
  BookingPayload,
  BookingPayloadResponse,
  BookingUpdateParams,
  CursorPaginatedResponse,
  Order,
  OrderDetail,
  OrderPackageDeal,
  OrderSearchParams,
} from '@/libs/interfaces'
import { bookingsApiMock } from '@/libs/mocks/handlers/bookings'

const orderDetailToOrder = (detail: OrderDetail): Order => ({
  orderID: detail.orderID,
  orderNumber: detail.orderNumber,
  driver: detail.driver
    ? { ...detail.driver, driverID: detail.driver.driverID ?? detail.driverID }
    : detail.driverID
      ? { driverID: detail.driverID }
      : undefined,
  driverCar: detail.driverCarID ? { driverCarID: detail.driverCarID } : undefined,
  submissionTime: detail.submissionTime,
  submittedTime: detail.submittedTime,
  pickupTime: detail.pickupTime,
  pickedUpTime: detail.pickedUpTime,
  updatedAt: detail.updatedAt,
  orderStatus: detail.orderStatus,
  total: typeof detail.cost === 'number' ? [detail.cost] : undefined,
  currency: detail.currency ? [detail.currency] : undefined,
  services: detail.services,
  products: detail.products,
  orgID: detail.orgID,
  storeID: detail.storeID,
})

const bookingsApiReal = {
  create: async (body: Partial<BookingPayload>): Promise<BookingPayloadResponse> => {
    return await api
      .post<ApiResponse<BookingPayloadResponse>>(
        URLs.Bookings.create(body.orgID || '', body?.storeID || ''),
        body,
      )
      .then((response) => response.data.data)
  },
  getById: async (params: {
    orgID: string
    storeID: string
    orderID: string
  }): Promise<OrderDetail> => {
    const query = buildQueryParams({ orderIDs: params.orderID })
    const response = await api.get<{ message: string; orders: OrderDetail[] }>(
      URLs.Bookings.byID(params.orgID, query),
    )
    return response.data?.orders?.[0] || ({} as OrderDetail)
  },
  list: async (params: BookingListParams): Promise<Order[]> => {
    const { storeID, orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api.get<ApiResponse<BookingData>>(
      URLs.Bookings.list(orgID, storeID, query),
    )
    return response.data?.data.orders || []
  },
  listCursor: async (params: BookingListParams): Promise<CursorPaginatedResponse<Order>> => {
    const { orgID, storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<BookingData>>(URLs.Bookings.list(orgID, storeID, query))
      .then((response) => response?.data?.data)

    return {
      data: response?.orders || [],
      pageInfo: {
        first: response?.first,
        prev: response?.prev,
        cursor: response?.cursor,
        next: response?.next,
        last: response?.last,
      },
    }
  },

  search: async (params: OrderSearchParams): Promise<Order[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    // /orgs/{orgID}/orders responds with the flat OrderDetail shape (driverID/driverCarID
    // siblings, no driverCar object, cost/currency as scalars), not the nested Order shape
    // the UI reads (driver.driverID, driverCar.driverCarID, total/currency arrays).
    const response = await api.get<{ message: string; orders?: OrderDetail[] }>(
      URLs.Bookings.byID(orgID, query),
    )
    return (response.data?.orders ?? []).map(orderDetailToOrder)
  },

  delete: async (params: { orgID: string; orderID: string }): Promise<void> =>
    api
      .delete<ApiResponse<void>>(URLs.Bookings.delete(params.orgID, params.orderID))
      .then((response) => response.data.data),

  update: async (payload: BookingUpdateParams): Promise<OrderDetail> => {
    const { params, data } = payload
    const response = await api.patch<{
      message: string
      data: {
        order: OrderDetail
        cost: number
        services: BookedService[]
        products: BookedProducts[]
        packageDeals: OrderPackageDeal[]
      }
    }>(URLs.Bookings.update(params.orgID, params.storeID, params.orderID), data)

    const result = response.data?.data
    if (!result) return {} as OrderDetail

    return {
      ...result.order,
      cost: result.cost,
      services: result.services,
      products: result.products,
      packageDeals: result.packageDeals,
    }
  },
}

export const bookingsApi: typeof bookingsApiReal = __IS_MOCK_MODE__
  ? bookingsApiMock
  : bookingsApiReal
